import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BookOpen, Plus, Edit, Trash2, ChevronDown, ChevronUp, Bold, Italic, List, ListOrdered, FileText, TrendingUp, BarChart3 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const WORD_LIMIT = 3000;

const countWords = (html) => {
  const plain = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (!plain) return 0;
  return plain.split(/\s+/).length;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Simple rich text editor using contentEditable
const RichEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  // Set initial content only once
  React.useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || '';
      isInitialized.current = true;
    }
  }, [value]);

  // Reset when dialog reopens with new value
  React.useEffect(() => {
    isInitialized.current = false;
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
      isInitialized.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <button type="button" onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => execCmd('insertUnorderedList')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCmd('insertOrderedList')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        dir="ltr"
        onInput={handleInput}
        onPaste={handlePaste}
        className="min-h-[200px] max-h-[400px] overflow-y-auto px-4 py-3 text-sm text-slate-800 focus:outline-none"
        style={{ fontFamily: 'Figtree, sans-serif', lineHeight: '1.7', textAlign: 'left', direction: 'ltr', unicodeBidi: 'plaintext' }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};

export const JournalSection = ({ entries, onCreateEntry, onUpdateEntry, onDeleteEntry, isReadOnly }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const wordCount = countWords(content);
  const isOverLimit = wordCount > WORD_LIMIT;

  // Word count dashboard stats
  const journalStats = useMemo(() => {
    if (!entries || entries.length === 0) return { totalWords: 0, totalEntries: 0, avgWords: 0, longestEntry: 0 };
    let totalWords = 0;
    let longestEntry = 0;
    entries.forEach(e => {
      const wc = countWords(e.content || '');
      totalWords += wc;
      if (wc > longestEntry) longestEntry = wc;
    });
    return {
      totalWords,
      totalEntries: entries.length,
      avgWords: entries.length > 0 ? Math.round(totalWords / entries.length) : 0,
      longestEntry,
    };
  }, [entries]);

  const resetEditor = useCallback(() => {
    setShowEditor(false);
    setEditingEntry(null);
    setTitle('');
    setContent('');
  }, []);

  const openNewEntry = () => {
    setEditingEntry(null);
    setTitle('');
    setContent('');
    setShowEditor(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setTitle(entry.title);
    setContent(entry.content);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    if (isOverLimit) return;
    setSaving(true);
    try {
      if (editingEntry) {
        await onUpdateEntry(editingEntry.id, title, content);
      } else {
        await onCreateEntry(title, content);
      }
      resetEditor();
    } catch {
      // error handled by parent
    }
    setSaving(false);
  };

  const confirmDelete = (entry) => {
    setDeleteTarget(entry);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await onDeleteEntry(deleteTarget.id);
    setShowDeleteDialog(false);
    setDeleteTarget(null);
    if (expandedId === deleteTarget.id) setExpandedId(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 mb-6" data-testid="journal-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Journal
          </h2>
          <span className="text-sm text-slate-500">({entries.length} {entries.length === 1 ? 'entry' : 'entries'})</span>
        </div>
        {!isReadOnly && (
          <Button onClick={openNewEntry} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="new-journal-entry">
            <Plus className="w-4 h-4 mr-1" /> New Entry
          </Button>
        )}
      </div>

      {/* Word Count Dashboard */}
      {entries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="journal-word-count-dashboard">
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <FileText className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-medium text-blue-600">Total Words</span>
            </div>
            <p className="text-xl font-bold text-blue-700" data-testid="journal-total-words">{journalStats.totalWords.toLocaleString()}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <BookOpen className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs font-medium text-emerald-600">Entries</span>
            </div>
            <p className="text-xl font-bold text-emerald-700" data-testid="journal-total-entries">{journalStats.totalEntries}</p>
          </div>
          <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2.5 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <BarChart3 className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-xs font-medium text-violet-600">Avg / Entry</span>
            </div>
            <p className="text-xl font-bold text-violet-700" data-testid="journal-avg-words">{journalStats.avgWords.toLocaleString()}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-amber-600">Longest</span>
            </div>
            <p className="text-xl font-bold text-amber-700" data-testid="journal-longest-entry">{journalStats.longestEntry.toLocaleString()}</p>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-4">
          {isReadOnly ? 'No journal entries yet.' : 'Start writing your first journal entry.'}
        </p>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <div key={entry.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`journal-entry-${entry.id}`}>
              <div
                className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm truncate">{entry.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{formatDate(entry.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  {!isReadOnly && (
                    <>
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                        onClick={(e) => { e.stopPropagation(); openEditEntry(entry); }}
                        data-testid={`edit-journal-${entry.id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); confirmDelete(entry); }}
                        data-testid={`delete-journal-${entry.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  {expandedId === entry.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedId === entry.id && (
                <div className="px-4 py-3 border-t border-slate-200">
                  <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: entry.content }} />
                  {entry.updated_at && entry.updated_at !== entry.created_at && (
                    <p className="text-xs text-slate-400 mt-3 italic">Last edited: {formatDate(entry.updated_at)}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={(v) => { if (!v) resetEditor(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Entry title..."
                data-testid="journal-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
              <RichEditor
                value={content}
                onChange={setContent}
                placeholder="Write your journal entry..."
              />
              <div className={`flex justify-end mt-1 text-xs ${isOverLimit ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                {wordCount.toLocaleString()} / {WORD_LIMIT.toLocaleString()} words
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={resetEditor} data-testid="journal-cancel">Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={!title.trim() || isOverLimit || saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="journal-save"
              >
                {saving ? 'Saving...' : editingEntry ? 'Update Entry' : 'Save Entry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Journal Entry</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 py-2">
            Are you sure you want to delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} data-testid="cancel-delete-journal">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-journal">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
